import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import i18n from 'i18next';
import {
  SessionStorageHelper,
  SessionStorageItemKeys
} from 'helper/StorageHelper';
import { useCallAPI } from 'hooks/useCallAPI';
import { SortOrder } from 'components/common/Table/types';
import { Pagination } from 'core/pagination/Pagination';
import { debounce, uniq } from 'lodash';
import { useCoreContext } from 'contexts/coreContext';
import { isBeaconAdmin, isBeaconExcecutive } from 'helper/ActorHelper';
import {
  getDefaultColumnDescription,
  getNameIdColumn,
  getStatusDesColumn
} from 'components/TableColumn/TableColumn';
import {
  BeaconMessageManager,
  DefaultBeaconMessageManager
} from 'core/beaconMessage/BeaconMessageManager';
import {
  BeaconConnectionOption,
  BeaconMessageListDTO,
  BeaconMessagesWithPagination
} from 'core/beaconMessage/BeaconMessage';
import { AgencyManager, DefaultAgencyManager } from 'core';
import moment from 'moment';
import { CampaignState } from 'core/rtbCampaign/RtbCampaign';
import { State } from 'core/order/Order';

enum BEACON_MESSAGE_COLUMNS {
  NAME = 'l2ObjectId',
  MERCHANT = 'clientName',
  BEACON_CONNECTION_NAME = 'beaconConnectionName',
  STATE = 'state',
  END_DATE = 'endDate'
}

const SIZE_PER_PAGE = 10;

const beaconMessageManager: BeaconMessageManager =
  new DefaultBeaconMessageManager();
const agencyManager: AgencyManager = new DefaultAgencyManager();

export const useBeaconMessageListModel = () => {
  const { loading, callAPIs } = useCallAPI();
  const [optionInited, setOptionInited] = useState(false);
  const [agencies, setAgencies] = useState<SelectOptions[]>();
  const [beaconConnections, setBeaconConnections] =
    useState<BeaconConnectionOption[]>();
  const [sortField, setSortField] = useState<string>(
    BEACON_MESSAGE_COLUMNS.NAME
  );
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [beaconMessagesWithPagination, setBeaconMessagesWithPagination] =
    useState<BeaconMessagesWithPagination>();
  const [pagination, setPagination] = useState<Pagination>();
  const [searchString, setSearchString] = useState<string>('');

  const core = useCoreContext();
  const actor = core?.authenticationManager.actor;
  const isBeaconAdminActor = actor ? isBeaconAdmin(actor) : false;
  const isBeaconExecutiveActor = actor ? isBeaconExcecutive(actor) : false;
  const defaultAgencyId = isBeaconAdminActor
    ? SessionStorageHelper.getNumberItem(SessionStorageItemKeys.AGENCY)
    : actor?.agencyId === null
      ? undefined
      : actor?.agencyId;
  const [agencyId, setAgencyId] = useState<number | undefined>(defaultAgencyId);
  const [beaconConnectionId, setBeaconConnectionId] = useState<
    number | undefined
  >(
    SessionStorageHelper.getNumberItem(SessionStorageItemKeys.BEACON_CONNECTION)
  );

  const [selectedBeaconMessages, setSelectedBeaconMessages] = useState<
    number[]
  >([]);

  const init = useCallback(
    (
      page: number | undefined = 1,
      overrides?: {
        searchString?: string;
        sortField?: string;
        sortOrder?: SortOrder;
      }
    ) => {
      const effectiveSortField = overrides?.sortField ?? sortField;
      const effectiveSortOrder = overrides?.sortOrder ?? sortOrder;
      const effectiveSearchString = overrides?.searchString ?? searchString;
      const pageable = {
        page,
        sizePerPage: SIZE_PER_PAGE,
        sort: effectiveSortField,
        direction: effectiveSortOrder
      };
      callAPIs(
        [
          () =>
            beaconMessageManager.getBeaconMessages(
              agencyId,
              beaconConnectionId,
              pageable,
              effectiveSearchString
            )
        ],
        (beaconMessagesWithPagination) => {
          setBeaconMessagesWithPagination(beaconMessagesWithPagination);
          setPagination(beaconMessagesWithPagination.pagination);
        }
      );
    },
    [agencyId, beaconConnectionId, sortField, sortOrder, searchString, callAPIs]
  );

  useEffect(() => {
    if (optionInited) {
      return;
    }
    if (isBeaconAdminActor) {
      callAPIs(
        [
          () => agencyManager.getAgenciesOptions(),
          () => beaconMessageManager.getBeaconConnectionOptions()
        ],
        (agencies, beaconConnections) => {
          setAgencies(agencies);
          setBeaconConnections(beaconConnections);
          setOptionInited(true);
        }
      );
      return;
    }
    callAPIs(
      [() => beaconMessageManager.getBeaconConnectionOptions()],
      (beaconConnections) => {
        setBeaconConnections(beaconConnections);
        setOptionInited(true);
      }
    );
  }, [isBeaconAdminActor, optionInited, init, callAPIs]);

  const initRef = useRef(init);
  initRef.current = init;

  useEffect(() => {
    initRef.current();
  }, [agencyId, beaconConnectionId]);

  const debouncedInit = useMemo(
    () =>
      debounce(
        (overrides?: {
          searchString?: string;
          sortField?: string;
          sortOrder?: SortOrder;
        }) => init(1, overrides),
        1000
      ),
    [init]
  );

  const sortDescriptor = useMemo(() => {
    return [
      {
        dataField: sortField,
        order: sortOrder
      }
    ] as [
      {
        dataField: string;
        order: SortOrder;
      }
    ];
  }, [sortField, sortOrder]);

  const sortBeaconMessages = useCallback(
    (field: string, direction: SortOrder) => {
      const currentSortSetting = sortDescriptor[0];
      if (
        field === currentSortSetting.dataField &&
        direction === currentSortSetting.order
      ) {
        return;
      }
      setSortField(field);
      setSortOrder(direction);
      init(1, { sortField: field, sortOrder: direction });
    },
    [sortDescriptor, init]
  );

  const handleOnSearch = useCallback(
    (searchString: string) => {
      setSearchString(searchString);
      if (searchString === '') {
        debouncedInit && debouncedInit.cancel();
        init(1, { searchString });
      } else {
        debouncedInit({ searchString });
      }
    },
    [init, debouncedInit]
  );

  const viewModelData = useMemo(() => {
    if (!beaconMessagesWithPagination) {
      return [];
    }
    return beaconMessagesWithPagination.beaconMessages;
  }, [beaconMessagesWithPagination]);

  const handleTableChange = useCallback(
    (type, props) => {
      if (type === 'sort') {
        sortBeaconMessages(props.sortField, props.sortOrder);
        return;
      }
      init(props.page);
    },
    [sortBeaconMessages, init]
  );

  const isBeaconEditable = useCallback(
    (beaconMessage: any) =>
      ![
        State.NOT_APPROVE,
        State.REJECT,
        State.SETTLE,
        State.SETTLED,
        State.CHANGE_PENDING
      ].includes(beaconMessage.orderState),
    []
  );

  const columnSettings = useMemo(() => {
    return [
      getNameIdColumn(
        getDefaultColumnDescription(
          BEACON_MESSAGE_COLUMNS.NAME,
          i18n.t('beaconMessageList.headers.name')
        ),
        isBeaconExecutiveActor
          ? (beaconMessage) =>
              isBeaconEditable(beaconMessage)
                ? `/beacon-messages/${beaconMessage.l2ObjectId}/edit`
                : undefined
          : undefined,
        undefined,
        'l2ObjectId'
      ),
      getDefaultColumnDescription(
        BEACON_MESSAGE_COLUMNS.MERCHANT,
        i18n.t('beaconMessageList.headers.merchant')
      ),
      getDefaultColumnDescription(
        BEACON_MESSAGE_COLUMNS.BEACON_CONNECTION_NAME,
        i18n.t('beaconMessageList.headers.beaconConnection')
      ),
      getStatusDesColumn(
        getDefaultColumnDescription(
          BEACON_MESSAGE_COLUMNS.STATE,
          i18n.t('beaconMessageList.headers.state'),
          false
        ),
        (beaconMessage) => {
          let des = '';
          let color;
          const now = moment();
          switch (beaconMessage.state) {
            case CampaignState.DEACTIVATE:
              des = i18n.t('campaignList.labels.deactivateState');
              color = 'black';
              break;
            case CampaignState.DELETE:
              des = i18n.t('campaignList.labels.deleteState');
              color = 'danger';
              break;
            case CampaignState.ACTIVATE:
              des = i18n.t('campaignList.labels.activateState');
              if (moment(beaconMessage.startDate).isAfter(now)) {
                des = i18n.t('campaignList.labels.notStartState');
                color = 'light';
              } else if (
                moment(beaconMessage.endDate)
                  .add(1, 'day')
                  .startOf('day')
                  .isBefore(now)
              ) {
                des = i18n.t('campaignList.labels.endState');
                color = 'light';
              }
              break;
            default:
              des = i18n.t('campaignList.labels.stoppingState');
              color = 'whiteTheme4';
              break;
          }

          return {
            des,
            color
          };
        }
      ),
      {
        ...getDefaultColumnDescription(
          BEACON_MESSAGE_COLUMNS.END_DATE,
          i18n.t('beaconMessageList.headers.endDate')
        )
      }
    ];
  }, [isBeaconExecutiveActor, isBeaconEditable]);

  const onHandleSelect = useCallback((id: string | number, select: boolean) => {
    setSelectedBeaconMessages((prev) =>
      select
        ? uniq([...prev, +id])
        : prev.filter((p) => p.toString() !== id.toString())
    );
  }, []);

  const onHandleSelectPage = useCallback(
    (select: boolean) => {
      const targetId = viewModelData.map((data) => data.l2ObjectId);
      setSelectedBeaconMessages((prev) =>
        select
          ? uniq([...prev, ...targetId])
          : prev.filter((p) => !targetId.includes(p))
      );
    },
    [viewModelData]
  );

  const clearSelect = useCallback(() => {
    setSelectedBeaconMessages([]);
  }, []);

  const updateBeaconMessagesState = useCallback(
    (state: 'activate' | 'deactivate') => {
      callAPIs(
        [
          () =>
            beaconMessageManager.updateBeaconMessagesState(
              selectedBeaconMessages,
              state
            )
        ],
        () => {
          setSelectedBeaconMessages([]);
          init();
        }
      );
    },
    [selectedBeaconMessages, init, callAPIs]
  );

  const batchOperations = useMemo(
    () => [
      {
        operation: {
          text: i18n.t('beaconMessageList.buttons.activate'),
          enable: selectedBeaconMessages.length > 0,
          hint: '',
          onClick: () => updateBeaconMessagesState('activate')
        }
      },
      {
        operation: {
          text: i18n.t('beaconMessageList.buttons.deactivate'),
          enable: selectedBeaconMessages.length > 0,
          hint: '',
          onClick: () => updateBeaconMessagesState('deactivate')
        }
      }
    ],
    [updateBeaconMessagesState, selectedBeaconMessages]
  );

  const getReportPath = useCallback((beaconMessage: BeaconMessageListDTO) => {
    const startDate = moment(beaconMessage.startDate)
      .startOf('day')
      .format('YYYY-MM-DD HH:mm:ss');
    const endDate = moment(beaconMessage.endDate)
      .endOf('day')
      .format('YYYY-MM-DD HH:mm:ss');
    const from = encodeURIComponent(startDate);
    const to = encodeURIComponent(endDate);
    return `/reports/performance?type=lineMessage&dimension=day&gran=day&from=${from}&to=${to}&l2ObjectId=${beaconMessage.l2ObjectId}`;
  }, []);

  return {
    loading,
    isBeaconAdminActor,
    isBeaconExecutiveActor,
    agencyId,
    setAgencyId,
    agencies,
    beaconConnections,
    beaconConnectionId,
    setBeaconConnectionId,
    beaconMessagesWithPagination,
    pagination,
    viewModelData,
    columnSettings,
    sortDescriptor,
    selectedBeaconMessages,
    batchOperations,
    handleTableChange,
    handleOnSearch,
    onHandleSelect,
    onHandleSelectPage,
    clearSelect,
    getReportPath,
    BEACON_MESSAGE_COLUMNS,
    SIZE_PER_PAGE
  };
};
